import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, ArrowLeft } from 'lucide-react';
import { formatCurrency } from '../utils/formatCurrency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateInvoice = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const defaultNotes = `Mode règlement/Payment mode
Payment via Bank Transfer/Payment via bank virement

Kein Ausweis der Umsatzsteuer (Reverse Charge)
Livraison intracommunautaire exonérée de taxe (Reverse Charge)`;

  const [formData, setFormData] = useState({
    customer_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    payment_term_days: 7,
    tax_rate: 0,
    notes: defaultNotes,
    period_of_performance: '',
    items: [{ description: '', quantity: 1, unit_price: 0, unit: 'hours' }]
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        items: formData.items.map(item => ({
          ...item,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price)
        }))
      };

      await axios.post(`${API}/invoices`, payload);
      toast.success('Rechnung erfolgreich erstellt');
      navigate('/invoices');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Rechnung');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, unit: 'hours' }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0));
    }, 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (parseFloat(formData.tax_rate) / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/invoices')}
          data-testid="back-button"
          className="self-start rounded-sm hover:bg-slate-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900" data-testid="create-invoice-title">
            Neue Rechnung
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Erstellen Sie eine neue Rechnung</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invoice Details */}
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Rechnungsdetails</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_id">Kunde *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                  required
                >
                  <SelectTrigger className="rounded-sm" data-testid="customer-select">
                    <SelectValue placeholder="Kunde auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.company_name} (#{customer.customer_number})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoice_date">Rechnungsdatum *</Label>
                <Input
                  id="invoice_date"
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                  required
                  data-testid="invoice-date-input"
                  className="rounded-sm font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment_term_days">Zahlungsfrist (Tage) *</Label>
                <Input
                  id="payment_term_days"
                  type="number"
                  value={formData.payment_term_days}
                  onChange={(e) => setFormData({ ...formData, payment_term_days: parseInt(e.target.value) })}
                  required
                  min="1"
                  data-testid="payment-term-input"
                  className="rounded-sm font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tax_rate">MwSt. (%) *</Label>
                <Input
                  id="tax_rate"
                  type="number"
                  step="0.01"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) })}
                  required
                  min="0"
                  data-testid="tax-rate-input"
                  className="rounded-sm font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="period_of_performance">Period of Performance</Label>
              <Input
                id="period_of_performance"
                value={formData.period_of_performance}
                onChange={(e) => setFormData({ ...formData, period_of_performance: e.target.value })}
                data-testid="period-of-performance-input"
                className="rounded-sm"
                placeholder="z.B. 01.12.2025 - 31.12.2025"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notizen (optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                data-testid="invoice-notes-input"
                className="rounded-sm"
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        {/* Invoice Items */}
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">Positionen</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addItem}
                data-testid="add-item-button"
                className="rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Position hinzufügen
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.items.map((item, index) => (
              <div key={index} className="p-4 bg-slate-50 rounded-sm border border-slate-200 space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Position {index + 1}</span>
                  {formData.items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(index)}
                      data-testid={`remove-item-${index}`}
                      className="h-8 w-8 rounded-sm hover:bg-slate-200"
                    >
                      <Trash2 className="w-4 h-4 text-slate-600" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor={`description-${index}`}>Beschreibung *</Label>
                    <Input
                      id={`description-${index}`}
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      required
                      data-testid={`item-description-${index}`}
                      className="rounded-sm bg-white"
                      placeholder="z.B. Call Agent Outbound"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`quantity-${index}`}>Menge *</Label>
                    <Input
                      id={`quantity-${index}`}
                      type="number"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      required
                      min="0"
                      data-testid={`item-quantity-${index}`}
                      className="rounded-sm font-mono bg-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`unit-${index}`}>Einheit *</Label>
                    <Select
                      value={item.unit}
                      onValueChange={(value) => updateItem(index, 'unit', value)}
                    >
                      <SelectTrigger className="rounded-sm bg-white" data-testid={`item-unit-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hours">Stunden</SelectItem>
                        <SelectItem value="days">Tage</SelectItem>
                        <SelectItem value="pieces">Stück</SelectItem>
                        <SelectItem value="units">Einheiten</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`unit_price-${index}`}>Einzelpreis (€) *</Label>
                    <Input
                      id={`unit_price-${index}`}
                      type="number"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                      required
                      min="0"
                      data-testid={`item-price-${index}`}
                      className="rounded-sm font-mono bg-white"
                    />
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Zwischensumme:</span>
                    <span className="font-mono font-bold text-slate-900">
                      {formatCurrency(parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Summary */}
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Zusammenfassung</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between items-center py-2">
              <span className="text-slate-600">Zwischensumme:</span>
              <span className="font-mono text-lg text-slate-900" data-testid="subtotal">
                {formatCurrency(calculateSubtotal())}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-slate-600">MwSt. ({formData.tax_rate}%):</span>
              <span className="font-mono text-lg text-slate-900" data-testid="tax-amount">
                {formatCurrency(calculateTax())}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-t-2 border-primary">
              <span className="font-bold text-slate-900">Gesamtbetrag:</span>
              <span className="font-mono text-2xl font-bold text-primary" data-testid="total-amount">
                {formatCurrency(calculateTotal())}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/invoices')}
            className="rounded-sm"
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            disabled={loading || !formData.customer_id || formData.items.length === 0}
            data-testid="save-invoice-button"
            className="rounded-sm bg-primary hover:bg-[#D62839] shadow-none"
          >
            {loading ? 'Erstellt...' : 'Rechnung erstellen'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateInvoice;